import React from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useCurrency } from "../contexts/CurrencyContext";
import { SEO } from "../components/SEO";

const TIERS = [
  {
    name: "AI SEO for SMB",
    priceUSD: 399,
    period: "/month",
    setupUSD: 399,
    description: "Perfect for Salons, Spas & Wellness",
    features: ["AI Receptionist (24/7)", "Reputation Management", "Social Media AI", "Listing Management", "Review Response AI", "Basic Reporting"],
    cta: "Get Started",
    popular: false
  },
  {
    name: "Starter",
    priceUSD: 799,
    period: "/month",
    setupUSD: 799,
    features: ["AI Receptionist (24/7)", "Reputation Management", "Local SEO & Listing Sync", "Social Marketing", "Email Campaigns", "AI-Powered Workflows", "CRM Access"],
    cta: "Get Started",
    popular: false
  },
  {
    name: "Growth",
    priceLabel: "Custom",
    period: "pricing",
    setupLabel: "Contact for details",
    features: ["Everything in Starter", "Reputation AI Premium", "Campaigns Pro", "Citation Builder", "Advanced Automations", "Competitive Benchmarking", "Priority Support"],
    cta: "Contact Sales",
    popular: true
  },
  {
    name: "Authority",
    priceLabel: "Custom",
    period: "pricing",
    setupLabel: "Contact for details",
    features: ["Everything in Growth", "Social Marketing Pro", "Multi-location Support", "White-label Options", "Dedicated Account Manager", "Custom Integrations", "Enterprise SLA"],
    cta: "Contact Sales",
    popular: false
  }
];

const PricingPage = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  return (
    <div className="pt-20" data-testid="pricing-page">
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for FunAsia Digital plans — AI SEO for SMB, Starter, Growth, and Authority. Choose the plan that fits your business."
        path="/pricing"
      />
      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-dark mb-4">Simple, Transparent Pricing</h1>
            <p className="text-gray-medium text-lg max-w-2xl mx-auto">Choose the plan that fits your business needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIERS.map((tier) => {
              const displayPrice = tier.priceUSD ? formatPrice(tier.priceUSD) : tier.priceLabel;
              const displaySetup = tier.setupUSD ? `${formatPrice(tier.setupUSD)} Setup Fee` : tier.setupLabel;
              return (
                <div
                  key={tier.name}
                  className={`relative bg-white rounded-2xl p-6 ${
                    tier.popular ? "ring-2 ring-orange-primary shadow-orange-lg lg:scale-105" : "shadow-card"
                  } card-hover`}
                  data-testid={`pricing-tier-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="gradient-orange text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>
                    </div>
                  )}
                  <h3 className="font-display text-xl font-bold text-gray-dark mb-2">{tier.name}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-bold text-orange-primary">{displayPrice}</span>
                    <span className="text-gray-medium text-sm">{tier.period}</span>
                  </div>
                  <p className="text-sm text-gray-medium mb-4">{displaySetup}</p>
                  {tier.description && <p className="text-sm text-gray-medium mb-4">{tier.description}</p>}
                  <ul className="space-y-2 mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-gray-dark">
                        <Check size={16} className="text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate("/store")}
                    className={`w-full py-3 rounded-full font-semibold transition-all text-sm ${tier.popular ? "btn-primary" : "btn-secondary"}`}
                  >
                    {tier.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
